import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { CATEGORIES, localizedLabel, localizedToolTitle } from '@/lib/toolCategories';
import { useAuth } from '@/hooks/useAuth';
import { isDesktop } from '@/lib/platform';
import AccountMenu from './AccountMenu';
import NotificationBell from './NotificationBell';
import '@/marketing/topnav.css';

const PRIMARY_CATEGORIES = ['organize', 'convert', 'edit', 'sign'];

export default function TopNav() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navRef = useRef(null);
  // Marketing-only static HTML pages (web/<page>.html) aren't reachable
  // from Electron — the protocol handler in desktop/main.js does exact-
  // file matching, then falls back to dist/index.html with no .html
  // extension lookup, so a desktop click lands on React's NotFound. Three
  // fix postures, picked per-link by what's actually useful in-app:
  //   /pricing → re-target to /account/billing (Billing.jsx renders
  //     dynamically per tier — Prompt 27.3 — so the upgrade funnel stays
  //     one click away).
  //   /teams/sales → hide (desktop users have already chosen Scrivar; team
  //     inquiries can still reach us via the in-app /contact form, which
  //     accepts the "Partnership" subject).
  //   /ai-pdf → hide (AI features are reachable directly via the Viewer's
  //     AI panel; a React mirror of marketing copy would just duplicate
  //     content with no in-app action).
  const desktop = isDesktop();
  const pricingHref = desktop ? '/account/billing' : '/pricing';

  useEffect(() => {
    if (!dropdownOpen) return undefined;
    const onClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setDropdownOpen(false); setActiveCategory(null);
      }
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') { setDropdownOpen(false); setActiveCategory(null); }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [dropdownOpen]);

  const openDropdown = (categoryId = null) => {
    setActiveCategory(categoryId);
    setDropdownOpen(true);
  };
  const closeDropdown = () => { setDropdownOpen(false); setActiveCategory(null); };

  return (
    <header className="topnav" ref={navRef}>
      <div className="topnav-inner">
        <a href="/" className="topnav-logo" aria-label="Scrivar home">
          <span className="mark">S</span>
          <span>Scrivar</span>
        </a>

        <ul className="topnav-links">
          <li>
            <button type="button" aria-haspopup="menu" aria-expanded={dropdownOpen}
              onClick={() => dropdownOpen ? closeDropdown() : openDropdown(null)}>
              {t('common:nav.tools') || 'Tools'}
              <ChevronDown size={14} />
            </button>
          </li>
          {PRIMARY_CATEGORIES.map((catId) => {
            const cat = CATEGORIES.find((c) => c.id === catId);
            if (!cat) return null;
            return (
              <li key={catId}>
                <button type="button"
                  className={`topnav-link${activeCategory === catId ? ' active' : ''}`}
                  onClick={() => openDropdown(catId)}>
                  {localizedLabel(t, cat)}
                </button>
              </li>
            );
          })}
          {!desktop && <li><a href="/ai-pdf" className="topnav-link">AI PDF</a></li>}
        </ul>

        <div className="topnav-spacer" />

        <div className="topnav-cta">
          <a href={pricingHref} className="btn btn-ghost">{t('common:nav.pricing') || 'Pricing'}</a>
          {!desktop && (
            <a href="/teams/sales" className="btn btn-ghost">{t('common:nav.teams') || 'Teams'}</a>
          )}
          {user ? (
            <>
              <a href="/tools" className="btn btn-secondary">{t('common:nav.open_app') || 'Open app'}</a>
              <NotificationBell />
              <AccountMenu />
            </>
          ) : (
            <>
              <a href="/login" className="btn btn-ghost">{t('common:nav.login') || 'Log In'}</a>
              <a href={pricingHref} className="btn btn-primary">{t('common:nav.start_trial') || 'Free Trial'}</a>
            </>
          )}
        </div>

        <button type="button" className="topnav-hamburger"
          aria-label="Open menu" aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}>
          <span /><span /><span />
        </button>
      </div>

      {dropdownOpen && (
        <ToolsDropdown t={t} activeCategory={activeCategory} onClose={closeDropdown} />
      )}
      {mobileOpen && (
        <MobileDrawer t={t} user={user} desktop={desktop} pricingHref={pricingHref} onClose={() => setMobileOpen(false)} />
      )}
    </header>
  );
}

function ToolsDropdown({ t, activeCategory, onClose }) {
  return (
    <div className="topnav-dropdown">
      <div className="topnav-dropdown-inner">
        {CATEGORIES.map((cat) => (
          <div className="topnav-col" data-active={activeCategory === cat.id} key={cat.id}>
            <h5 style={{ color: activeCategory === cat.id ? cat.colors.fg : undefined }}>
              {localizedLabel(t, cat)}
            </h5>
            <ul>
              {cat.tools.map((tool) => (
                <li key={tool.title}>
                  <a href={tool.path || '/tools'} className="topnav-tool"
                    style={{ '--tool-bg': cat.colors.bg, '--icon-bg': cat.colors.bg, '--icon-fg': cat.colors.fg }}
                    title={tool.description}
                    onClick={onClose}>
                    <span className="icon-wrap">
                      {tool.icon ? <tool.icon size={18} strokeWidth={1.75} /> : null}
                    </span>
                    <span className="label">{localizedToolTitle(t, tool)}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="topnav-foot">
        <a href="/tools" className="all-tools">
          {t('common:nav.view_all_tools') || 'View all 30+ tools'} →
        </a>
        <span className="free-note">
          {t('common:nav.free_note') || 'Free to use · 3 tasks per day, no account required'}
        </span>
      </div>
    </div>
  );
}

function MobileDrawer({ t, user, desktop = false, pricingHref = '/pricing', onClose }) {
  return (
    <div className="topnav-mobile">
      {CATEGORIES.map((cat) => (
        <details key={cat.id}>
          <summary>
            {localizedLabel(t, cat)}
            <ChevronDown size={14} />
          </summary>
          <div style={{ padding: '4px 8px 16px' }}>
            {cat.tools.map((tool) => (
              <a key={tool.title} href={tool.path || '/tools'} className="mobile-tool" onClick={onClose}>
                <span style={{ background: cat.colors.bg, color: cat.colors.fg, width: 24, height: 24, borderRadius: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {tool.icon ? <tool.icon size={14} strokeWidth={1.75} /> : null}
                </span>
                {localizedToolTitle(t, tool)}
              </a>
            ))}
          </div>
        </details>
      ))}
      <a href={pricingHref} className="mobile-link">{t('common:nav.pricing') || 'Pricing'}</a>
      {!desktop && (
        <a href="/teams/sales" className="mobile-link">{t('common:nav.teams') || 'Teams'}</a>
      )}
      {!desktop && <a href="/ai-pdf" className="mobile-link">AI PDF</a>}
      {user ? (
        <>
          <a href="/tools" className="mobile-link">{t('common:nav.open_app') || 'Open app'}</a>
          <a href="/account" className="mobile-link">{t('common:nav.my_account') || 'My account'}</a>
          <a href="/account/billing" className="mobile-link">{t('common:nav.billing') || 'Billing & plan'}</a>
          <a href="/cloud" className="mobile-link">{t('common:nav.cloud_documents') || 'Cloud documents'}</a>
          <a href="/activate" className="mobile-link">{t('common:nav.activate') || 'Activate code'}</a>
          <button type="button" data-auth-signout className="mobile-link" style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer' }}>
            {t('common:nav.sign_out') || 'Sign out'}
          </button>
        </>
      ) : (
        <>
          <a href="/login" className="mobile-link">{t('common:nav.login') || 'Log In'}</a>
          <a href={pricingHref} className="mobile-link" style={{ color: 'var(--tn-primary)', fontWeight: 600 }}>
            {t('common:nav.start_trial') || 'Free Trial'}
          </a>
        </>
      )}
    </div>
  );
}
